import type { Injector } from '@angular/core';
import type { DateTime } from 'luxon';
import type { Tachograph } from '../../interfaces/common/tachograph';
import type {
  TelematicsFleetData,
  TelematicsRouteData,
  TelematicsTelemetryData,
} from '../../interfaces/telematics/telematics-event';
import type { TelematicsItem } from '../../interfaces/telematics/telematics-item';
import { TelematicsDelegationFactory } from './telematics-delegation.factory';
import { TelematicsDriverFactory } from './telematics-driver.factory';
import { TelematicsEmptyDistanceFactory } from './telematics-empty-distance.factory';
import { TelematicsFleetFactory } from './telematics-fleet.factory';
import { TelematicsOrderFactory } from './telematics-order.factory';
import { TelematicsRoutePointFactory } from './telematics-route-point.factory';
import { TelematicsSwapFactory } from './telematics-swap.factory';

export class TelematicsItemFactory {
  public static from(data: {
    fleet: TelematicsFleetData;
    telemetry: TelematicsTelemetryData | null;
    route: TelematicsRouteData;
    tachographs: ReadonlyMap<string, Tachograph>;
    now: DateTime;
    previous: TelematicsItem | null;
    injector: Injector;
  }): TelematicsItem {
    const drivers = TelematicsDriverFactory.from({
      fleet: data.fleet,
      tachographs: data.tachographs,
    });
    const { activeDelegations, delegations, ganttDelegations } =
      TelematicsDelegationFactory.from({
        fleet: data.fleet,
        now: data.now,
        injector: data.injector,
      });
    const telemetry = data.telemetry?.telemetry ?? null;
    const routePoints = TelematicsRoutePointFactory.from({
      route: data.route,
      delegations,
    });
    const orders = TelematicsOrderFactory.from({
      routePoints,
      fleetUuid: data.fleet.uuid,
      now: data.now,
    });
    const emptyDistances = TelematicsEmptyDistanceFactory.from({
      routePoints,
      orders,
      delegations,
    });
    const swaps = TelematicsSwapFactory.from({
      routePoints,
      fleetUuid: data.fleet.uuid,
      now: data.now,
    });

    const fleet = TelematicsFleetFactory.from({
      fleet: data.fleet,
      telemetry,
      activeDelegations,
      routePoints,
      now: data.now,
      previous: data.previous,
      injector: data.injector,
    });

    return {
      ...fleet,
      drivers,
      activeDelegations,
      delegations,
      ganttDelegations,
      telemetry,
      routePoints,
      orders,
      emptyDistances,
      swaps,
    };
  }
}
